/**
 * PuzzleRoyaleLobbyPanel
 *
 * ENTRY surface for Puzzle Royale, rendered inline in the Fun Challenges
 * lobby's Multiplayer tab. It is options-only: Create (with race settings),
 * Join-by-code, Spectate-by-code. There is NO waiting room here — once a lobby
 * is created (or joined / opened via a share link) the royale store flips to
 * `lobby`/`in_battle` and the global listener navigates everyone to
 * PuzzleRoyaleBattle, which hosts the waiting room (3-tab right rail) + race.
 *
 * Share / deep links land on the Fun Challenges lobby with params
 * `?mode=multiplayer&join=<code>`; BattleLobby opens the Multiplayer tab and
 * passes the code here as `initialJoinCode` (prefilled into the Join field).
 */

import React, {useEffect, useState} from 'react';
import {ActivityIndicator, Pressable, StyleSheet, View} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import CCTextInput from '@/Atoms/CCTextInput/CCTextInput';
import SlideModal from '@/Atoms/SlideModal';
import CCButton from '@/Atoms/CCButton';
import {FontSizes, vs} from '@/Utils/Dimensions';

import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useRoyaleSocket from '@/Hooks/FunChallengesSocket/useRoyaleSocket';
import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';

import {DEFAULT_ROYALE_SETTINGS} from './royaleConstants';
import type {RoyaleSettings as RoyaleSettingsValue} from './royaleTypes';
import RoyaleSettings from './components/RoyaleSettings';
import {
  consumeClassroomRoyaleHandoff,
  type ClassroomRoyaleHandoff,
} from './util/classroomRoyaleHandoff';

const TEAL = '#3DAB9E';
const TEAL_DARK = '#1F6C63';
const TEAL_TINT_SOFT = 'rgba(61, 171, 158, 0.06)';
const TEXT_DARK = '#262322'; // used by primaryBtnText
const TEXT_MID = '#4D3F37';
const TEXT_MUTED = '#8A7E5F';
const YELLOW = '#FACF47';
const YELLOW_DARK = '#A9873B';
const BORDER = 'rgba(38, 35, 34, 0.10)';
const BORDER_STRONG = 'rgba(38, 35, 34, 0.18)';

type EntryMode = 'create' | 'join' | 'spectate';

interface PanelProps {
  /** Prefill the Join field (from a share/deep link); also selects the Join tab. */
  initialJoinCode?: string;
  /**
   * When true (and an initialJoinCode is present), join AUTOMATICALLY as soon as
   * the socket authenticates — no second click. Used by the classroom student
   * "Join Puzzle Royale" banner, which opens this tab and expects to land
   * straight in the battle. The global listener then navigates to the battle.
   */
  autoJoin?: boolean;
  /**
   * One-shot localStorage key (see classroomRoyaleHandoff). When present this
   * tab was opened by a coach from the classroom to auto-create a royale: we
   * consume the stashed {settings, classroom}, fire createLobby once, then POST
   * the resulting shareCode to Django so students are notified.
   *
   * NOTE: the classroom now creates the lobby instantly IN the classroom tab
   * (not via this key); this is kept for any legacy/deep-link path.
   */
  classroomAutoCreateKey?: string;
  /**
   * Classroom context carried on the coach's "Open Puzzle Royale" reopen URL
   * (?crMeeting=&crOccurence=). Sets the store's classroomContext so a REMATCH
   * started from this reopened tab re-registers with Django (notifies students).
   */
  classroomMeetingId?: string;
  classroomOccurenceId?: string;
}

export default function PuzzleRoyaleLobbyPanel({
  initialJoinCode,
  autoJoin,
  classroomAutoCreateKey,
  classroomMeetingId,
  classroomOccurenceId,
}: PanelProps) {
  // `loggedInUserstore` has no `name` field — the authoritative display name
  // lives on `userData` (username / name / first_name). Resolve it the same
  // way the socket layer does so the roster shows a real name, not "Player".
  const username = useLoggedInUserStore(
    s =>
      s.userData?.username ||
      s.userData?.name ||
      s.userData?.first_name ||
      s.email ||
      '',
  );
  const myIcon = useLoggedInUserStore(s => s.icon);

  const {ready, authed, createLobby, joinLobby, spectate, probeSession} =
    useRoyaleSocket();

  // Royale errors (e.g. ROOM_NOT_WAITING when joining a code after the race
  // started) surface here as a dismissible banner.
  const errorMessage = useFunChallengesRoyaleStore(s => s.errorMessage);
  const clearError = useFunChallengesRoyaleStore(s => s.clearError);

  // ── Entry view state ────────────────────────────────────────────────────
  const [entryMode, setEntryMode] = useState<EntryMode>(
    initialJoinCode ? 'join' : 'create',
  );
  const [joinCode, setJoinCode] = useState<string>(initialJoinCode ?? '');
  const [settings, setSettings] = useState<RoyaleSettingsValue>(
    DEFAULT_ROYALE_SETTINGS,
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  // A share/deep link prefills the code + opens the Join tab.
  useEffect(() => {
    if (initialJoinCode) {
      setEntryMode('join');
      setJoinCode(initialJoinCode);
    }
  }, [initialJoinCode]);

  // Classroom student "Join Puzzle Royale": auto-join the moment the socket is
  // authed — no second click. Fire ONCE (ref-guarded). On success the store
  // flips to lobby/in_battle and the global listener navigates to the battle.
  const autoJoinFiredRef = React.useRef(false);
  useEffect(() => {
    if (
      !autoJoin ||
      !initialJoinCode ||
      autoJoinFiredRef.current ||
      !(ready && authed)
    ) {
      return;
    }
    autoJoinFiredRef.current = true;
    // Probe for an EXISTING session FIRST. If this user is already a seated
    // player in a running race (e.g. they reopened the tournament link mid-race,
    // so the tournament screen auto-routed them here again), the server now
    // deliberately answers the join with SILENCE rather than converting them
    // into a spectator — so without this probe the store would stay empty and
    // they'd be stranded on the lobby screen. probeSession resolves their active
    // session and the global listener then navigates them back into the race.
    // Harmless when there is no active session (the server replies
    // NO_ACTIVE_SESSION, which the socket layer swallows for probe requests).
    probeSession();
    joinLobby(initialJoinCode.trim(), {
      asSpectator: false,
      username,
      avatarUrl: typeof myIcon === 'string' ? myIcon : undefined,
    });
    // RE-PROBE on a short cadence. A single probe is not enough for a TOURNAMENT
    // start: the client auto-routes here the moment the tournament flips to
    // `live` (broadcastTournamentState), but the server is still inside
    // beginSession's ~2.8s puzzle-sequence fetch, so the session does not exist
    // yet and both the join and this first probe legitimately resolve to
    // nothing. fun_session_start does arrive on its own — these retries are the
    // belt-and-braces path for a dropped/missed envelope, and they stop as soon
    // as the store leaves the empty state. Bounded so a genuinely dead code
    // can't spin forever.
    let probes = 0;
    const probeTimer = setInterval(() => {
      probes += 1;
      const st = useFunChallengesRoyaleStore.getState();
      // Once we're in a lobby or a battle, the store is seeded — stop probing.
      if (st.status !== 'idle' || probes > 8) {
        clearInterval(probeTimer);
        return;
      }
      probeSession();
    }, 1200);
    return () => clearInterval(probeTimer);
  }, [
    autoJoin,
    initialJoinCode,
    ready,
    authed,
    joinLobby,
    probeSession,
    username,
    myIcon,
  ]);

  // ── Classroom auto-create (coach opened this tab from the classroom) ──────
  // Consume the handoff ONCE on mount (read-and-delete) so a refresh can't make
  // a second lobby. We then create the lobby as soon as the socket is authed.
  // Registering the lobby with Django (to notify students) happens on the BATTLE
  // screen, which is the single registration point for both the initial lobby
  // AND every rematch lobby (#2) — see PuzzleRoyaleBattle. Here we just stash the
  // classroom origin in the store so it survives the lobby→battle→rematch chain.
  const [handoff, setHandoff] = useState<ClassroomRoyaleHandoff | null>(null);
  const autoCreateFiredRef = React.useRef(false);

  useEffect(() => {
    if (classroomAutoCreateKey && !handoff) {
      const consumed = consumeClassroomRoyaleHandoff(classroomAutoCreateKey);
      if (consumed) {
        setHandoff(consumed);
        setEntryMode('create');
        useFunChallengesRoyaleStore.getState().setClassroomContext({
          meetingId: consumed.meetingId,
          occurenceId: consumed.occurenceId,
        });
      }
    }
  }, [classroomAutoCreateKey, handoff]);

  // Coach reopening a classroom royale (?crMeeting=&crOccurence=): restore the
  // classroom origin so a rematch from this tab re-registers with Django.
  useEffect(() => {
    if (classroomMeetingId && classroomOccurenceId) {
      useFunChallengesRoyaleStore.getState().setClassroomContext({
        meetingId: classroomMeetingId,
        occurenceId: classroomOccurenceId,
      });
    }
  }, [classroomMeetingId, classroomOccurenceId]);

  // #3 — cut the "promoted to host" delay. The reopen URL (crMeeting/crOccurence
  // + a join code) ALWAYS means the coach opening THEIR OWN room as host, so we
  // can optimistically mark self as host the moment the optimistic lobby exists
  // — the Start button shows immediately instead of waiting for the server's
  // fun_room_update round-trip. The server snapshot still arrives and confirms
  // the same hostId, so this only removes the lag, never asserts a wrong host.
  const reopenLobby = useFunChallengesRoyaleStore(s => s.lobby);
  const hostSeededRef = React.useRef(false);
  useEffect(() => {
    if (
      !classroomMeetingId ||
      hostSeededRef.current ||
      !reopenLobby?.roomId ||
      reopenLobby.hostId // server already set it
    ) {
      return;
    }
    const me = String(
      useFunChallengesRoyaleStore.getState().myUserId || '',
    );
    if (!me) {
      return;
    }
    hostSeededRef.current = true;
    useFunChallengesRoyaleStore.getState().setLobby(
      {
        ...reopenLobby,
        hostId: me,
        players: (reopenLobby.players || []).map(p =>
          String(p.userId) === me ? {...p, isHost: true} : p,
        ),
      },
      me,
    );
  }, [classroomMeetingId, reopenLobby]);

  // Fire createLobby once, after auth, with the coach's chosen settings.
  useEffect(() => {
    if (!handoff || autoCreateFiredRef.current || !(ready && authed)) {
      return;
    }
    autoCreateFiredRef.current = true;
    createLobby({
      hostIsPlayer: true,
      settings: handoff.settings,
      creatorName: username,
      creatorAvatarUrl: typeof myIcon === 'string' ? myIcon : undefined,
    });
  }, [handoff, ready, authed, createLobby, username, myIcon]);

  // Used only to gate the "creating…" loader until the lobby exists in the store.
  const lobbyForRegister = useFunChallengesRoyaleStore(s => s.lobby);

  const topicsChosen = settings.topics.length > 0;

  const handleCreate = () => {
    createLobby({
      // Default to playing; the host can switch to spectate in the battle
      // screen's waiting room any time before starting.
      hostIsPlayer: true,
      settings,
      creatorName: username,
      creatorAvatarUrl: typeof myIcon === 'string' ? myIcon : undefined,
    });
    // The server's fun_room_created flips the royale store to `lobby`; the
    // global listener then navigates to PuzzleRoyaleBattle.
  };

  const handleJoin = (asSpectator: boolean) => {
    const code = joinCode.trim();
    if (!code) {
      return;
    }
    if (asSpectator) {
      // Dedicated spectate path: the server replies fun_spectate_start (a
      // read-only session bootstrap) whether the race is already running or
      // not — no participant join, no scoring. Pass our name so the spectator
      // roster shows it (#1). The global listener navigates to
      // PuzzleRoyaleBattle on the resulting in_battle/lobby status.
      spectate(code, {
        username,
        avatarUrl: typeof myIcon === 'string' ? myIcon : undefined,
      });
      return;
    }
    joinLobby(code, {
      asSpectator,
      username,
      avatarUrl: typeof myIcon === 'string' ? myIcon : undefined,
    });
  };

  // Share/deep-link cold open: the socket authenticates a beat after mount.
  // We DON'T auto-join (the user may want to spectate, not play) — instead we
  // gate the entry UI behind a CCLoader until `ready && authed`, then reveal
  // the Join/Spectate options with the code prefilled so they choose. This
  // also avoids the "auth failed" flash + a join firing before auth completes.
  // Show the loader for: a deep-link cold open (until authed), a classroom
  // auto-create in flight (consumed handoff, no lobby yet), or a classroom
  // auto-join in flight (autoJoin + code, until the store has a lobby/session
  // and the global listener navigates away).
  const connecting =
    (!!initialJoinCode && !(ready && authed)) ||
    (!!handoff && !lobbyForRegister?.roomId) ||
    (!!autoJoin && !!initialJoinCode && !lobbyForRegister?.roomId);

  // NOTE: the WAITING ROOM (roster, host start, play/spectate toggle, share
  // link) now lives on PuzzleRoyaleBattle (3-tab right rail). This panel is
  // ENTRY-ONLY: create options + join-by-code + spectate-by-code. On create /
  // join the royale store flips to `lobby`/`in_battle` and the global listener
  // navigates everyone to PuzzleRoyaleBattle.

  // ── Connecting (deep-link cold open): loader until auth completes ────────
  // CCLoader's Lottie is sized to a fraction of the WINDOW, so it overflows /
  // clips inside a compact panel slot. Use a plain ActivityIndicator (clean,
  // self-sized) for the inline spinner + our own caption.
  if (connecting) {
    return (
      <View style={[styles.panel, styles.connectingPanel]}>
        <ActivityIndicator size="large" color={TEAL_DARK} />
        <CCText style={styles.connectingTitle}>
          {handoff
            ? 'Creating your puzzle royale…'
            : autoJoin
            ? 'Joining the puzzle royale…'
            : 'Reconnecting you to the race…'}
        </CCText>
        <CCText style={styles.connectingHint}>
          {handoff
            ? 'Setting up the lobby and notifying your students.'
            : autoJoin
            ? 'Taking you to the race — hang tight.'
            : "Almost there — you'll be able to join or spectate in a moment."}
        </CCText>
      </View>
    );
  }

  // ── ENTRY ───────────────────────────────────────────────────────────────
  return (
    <View style={styles.panel}>
      {!!errorMessage && (
        <Pressable onPress={clearError} style={styles.errorBanner}>
          <CCText style={styles.errorBannerText}>{errorMessage}</CCText>
          <CCText style={styles.errorBannerDismiss}>Dismiss</CCText>
        </Pressable>
      )}
      <View style={styles.tabRow}>
        {(
          [
            {mode: 'create', label: 'Create'},
            {mode: 'join', label: 'Join'},
            {mode: 'spectate', label: 'Spectate'},
          ] as Array<{mode: EntryMode; label: string}>
        ).map(t => (
          <Pressable
            key={t.mode}
            onPress={() => setEntryMode(t.mode)}
            style={[styles.tab, entryMode === t.mode && styles.tabActive]}>
            <CCText
              style={[
                styles.tabText,
                entryMode === t.mode && styles.tabTextActive,
              ]}>
              {t.label}
            </CCText>
          </Pressable>
        ))}
      </View>

      {entryMode === 'create' && (
        <Card title="Host a race">
          <Pressable
            onPress={() => setSettingsOpen(true)}
            style={({pressed}) => [
              styles.settingsCard,
              pressed && styles.settingsCardPressed,
            ]}>
            <View style={styles.settingsChips}>
              <Chip
                icon="tag-multiple"
                text={
                  topicsChosen
                    ? `${settings.topics.length} topic${
                        settings.topics.length === 1 ? '' : 's'
                      }`
                    : 'Any topic'
                }
              />
              {/* raw → keep "120s" lowercase (chipText otherwise capitalizes). */}
              <Chip icon="timer-outline" text={`${settings.timerSec}s`} raw />
              <Chip icon="speedometer" text={settings.difficulty} />
              <Chip
                icon="alert-octagon-outline"
                text={
                  settings.punish
                    ? `Punish ${settings.punishThreshold ?? 5}`
                    : 'No punish'
                }
              />
            </View>
            <CCText style={styles.settingsHint}>Tap to edit ›</CCText>
          </Pressable>

          <CCText style={styles.createNote}>
            You'll choose whether to play or just spectate in the lobby, before
            the race starts.
          </CCText>

          <PrimaryButton
            label={!ready ? 'Connecting…' : 'Create lobby'}
            disabled={!ready}
            onPress={handleCreate}
          />
        </Card>
      )}

      {entryMode === 'join' && (
        <Card title="Join with a code">
          <CCTextInput
            value={joinCode}
            onChangeText={setJoinCode}
            placeholder="Enter join code"
            autoCapitalize="characters"
          />
          <PrimaryButton
            label={!ready ? 'Connecting…' : 'Join race'}
            disabled={!ready || !joinCode.trim()}
            onPress={() => handleJoin(false)}
          />
        </Card>
      )}

      {entryMode === 'spectate' && (
        <Card title="Spectate a race">
          <CCText style={styles.spectateHint}>
            Enter a join code to watch the race and follow any player live.
          </CCText>
          <CCTextInput
            value={joinCode}
            onChangeText={setJoinCode}
            placeholder="Enter join code"
            autoCapitalize="characters"
          />
          <PrimaryButton
            label={!ready ? 'Connecting…' : 'Watch race'}
            disabled={!ready || !joinCode.trim()}
            onPress={() => handleJoin(true)}
          />
        </Card>
      )}

      <SlideModal
        visible={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        modalHeaderTitle="Race settings"
        type="center"
        contentPadding={20}>
        <RoyaleSettings value={settings} onChange={setSettings} />
        <CCButton
          type="fill"
          stretched
          onPress={() => setSettingsOpen(false)}
          style={{marginTop: 16}}>
          Submit
        </CCButton>
      </SlideModal>
    </View>
  );
}

function Card({title, children}: {title?: string; children: React.ReactNode}) {
  return (
    <Box style={styles.card}>
      {title ? <CCText style={styles.cardTitle}>{title}</CCText> : null}
      {children}
    </Box>
  );
}

function Chip({
  text,
  icon,
  warn,
  raw,
}: {
  text: string;
  /** MaterialCommunityIcons glyph shown before the text. */
  icon?: string;
  warn?: boolean;
  /** Skip the capitalize transform (for values like "120s" that must stay as-is). */
  raw?: boolean;
}) {
  return (
    <View style={[styles.chip, warn && styles.chipWarn]}>
      {icon ? (
        <MaterialCommunityIcons
          name={icon}
          size={13}
          color={warn ? '#E17846' : TEAL_DARK}
          style={styles.chipIcon}
        />
      ) : null}
      <CCText
        style={[
          styles.chipText,
          raw && styles.chipTextRaw,
          warn && styles.chipTextWarn,
        ]}>
        {text}
      </CCText>
    </View>
  );
}

function PrimaryButton({
  label,
  disabled,
  onPress,
}: {
  label: string;
  disabled?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      style={({pressed}) => [
        styles.primaryBtn,
        disabled && styles.primaryBtnDisabled,
        !disabled && pressed && styles.primaryBtnPressed,
      ]}>
      <CCText
        style={[
          styles.primaryBtnText,
          disabled && styles.primaryBtnTextDisabled,
        ]}>
        {label}
      </CCText>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  // Rendered inside the Multiplayer tab's section (BattleLobby owns the page
  // ScrollView + header), so this is just a vertical stack with gaps.
  panel: {gap: 18, width: '100%'},
  connectingPanel: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: vs(40),
    gap: 8,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    backgroundColor: 'rgba(205,44,57,0.08)',
    borderWidth: 1,
    borderColor: 'rgba(205,44,57,0.25)',
    borderRadius: 10,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  errorBannerText: {
    flex: 1,
    fontSize: FontSizes[13] || 13,
    fontWeight: '600',
    color: '#A3242E',
  },
  errorBannerDismiss: {
    fontSize: FontSizes[12] || 12,
    fontWeight: '700',
    color: '#A3242E',
    textDecorationLine: 'underline',
  },
  connectingTitle: {
    fontSize: FontSizes[16] || 16,
    fontWeight: '700',
    color: TEXT_MID,
    textAlign: 'center',
  },
  connectingHint: {
    fontSize: FontSizes[13] || 13,
    color: TEXT_MUTED,
    textAlign: 'center',
  },
  tabRow: {
    flexDirection: 'row',
    gap: 6,
    backgroundColor: '#F4EEDB',
    borderRadius: 12,
    padding: 5,
  },
  tab: {
    flex: 1,
    paddingVertical: 11,
    borderRadius: 9,
    alignItems: 'center',
  },
  tabActive: {backgroundColor: TEAL},
  tabText: {
    fontSize: FontSizes[14] || 14,
    fontWeight: '700',
    color: TEXT_MUTED,
  },
  tabTextActive: {color: CCColors.White},
  card: {
    padding: 18,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CCColors.White,
    gap: vs(14),
    shadowColor: '#4D3F37',
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: {width: 0, height: 3},
  },
  cardTitle: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEXT_MID,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  settingsCard: {
    padding: 16,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: TEAL_TINT_SOFT,
    gap: 10,
  },
  settingsCardPressed: {borderColor: BORDER_STRONG},
  settingsChips: {flexDirection: 'row', gap: 8, flexWrap: 'wrap'},
  settingsHint: {
    fontSize: FontSizes[12] || 12,
    color: TEXT_MUTED,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(61, 171, 158, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(61, 171, 158, 0.30)',
  },
  chipIcon: {marginTop: 0.5},
  chipWarn: {
    backgroundColor: 'rgba(225, 120, 70, 0.12)',
    borderColor: 'rgba(225, 120, 70, 0.30)',
  },
  chipText: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEAL_DARK,
    textTransform: 'capitalize',
  },
  // No transform — for values like "120s" that must render exactly as given
  // (the default `capitalize` was rendering "120s" as "120S").
  chipTextRaw: {textTransform: 'none'},
  chipTextWarn: {color: '#E17846'},
  createNote: {
    fontSize: FontSizes[12] || 12,
    color: TEXT_MUTED,
    lineHeight: 17,
  },
  spectateHint: {
    fontSize: FontSizes[13] || 13,
    color: TEXT_MID,
    lineHeight: 18,
  },
  primaryBtn: {
    backgroundColor: YELLOW,
    borderRadius: 14,
    borderBottomWidth: 4,
    borderColor: YELLOW_DARK,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnPressed: {borderBottomWidth: 2, paddingVertical: 18},
  primaryBtnDisabled: {backgroundColor: '#F2E8C7', borderColor: '#D9C796'},
  primaryBtnText: {
    fontSize: FontSizes[15] || 15,
    fontWeight: '800',
    color: TEXT_DARK,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  primaryBtnTextDisabled: {color: TEXT_MUTED},
});
